/**
 * imageExtractors — Image_Intelligence_Analyzer IIA-2 (row 0.99253).
 *
 * Per-kind structured extractors that sit on top of IIA-1's
 * `analyzeImageEvidence` + `classifyImageKind` (see
 * `./analyzeImageEvidence.ts`). Each extractor:
 *
 *   1. takes an image Blob + a classified `ImageKind`,
 *   2. dispatches to a kind-specific Gemini-multimodal prompt
 *      (reusing IIA-1's `defaultMultimodalRunner` — never forking),
 *   3. validates the response against a zod schema,
 *   4. returns either a typed result OR a structured failure record.
 *
 * The four structured kinds the pack calls out:
 *   - `'flood-map'`        → FIRM panel    (`bfeFt`, `floodZone`, `datum`, `panelId`, `effectiveDate`)
 *   - `'borehole-log'`     → Borehole log  (`uscsClass`, `bearingCapacityKPa`, `boreholeDepthM`, `groundwaterDepthM`, `layers[]`)
 *   - `'survey-plat'`      → Site map      (`parcelId`, `recordedAcreage`, `dimensions[]`, `easements[]`, `bearings[]`)
 *   - `'one-line-diagram'` → Utility diag  (`substationName`, `voltageKv`, `capacityMw`, `utilityName`, `interconnectionPoint`)
 *
 * Topo / arch-plan / permit-scan / site-photo / aerial / kmz-overlay /
 * protest-flyer DEFER to IIA-3 free-text fallback (no per-kind schema
 * this turn — the dispatcher returns `kind: 'extraction-failure'` with
 * `reason: 'no-structured-extractor'` and the caller writes the
 * Gemini summary into `siteContext.claims[]` as a free-text
 * `'image-observation'`).
 *
 * Pipeline integration (IIA-4): `documentUpload.ts:processUploadedFile`
 * calls `dispatchImageExtraction` after the file is persisted and the
 * SiteDocument is built. The dispatcher classifies, extracts, and
 * stamps the result onto `siteDoc.iiaResult`. The Coverage Matrix
 * resolver consumes `iiaResult` indirectly: when the doc tier picks
 * this doc as the strongest match for a requirement, the resolver
 * stamps `extractionModality: 'vision'` on the resulting envelope so
 * the CAF-4 chip renderer flips to the `📷 Image (vision)` variant.
 *
 * Cost discipline:
 *   - The classifier + extraction passes both go through IIA-1's
 *     `defaultMultimodalRunner`, which already has a per-call timeout
 *     + structured-JSON enforcement + injectable runner. We DO NOT
 *     add a second cost cap here — pack hard-rule "defer to IIA-1's
 *     existing budget cap".
 *   - Failure modes degrade gracefully: every extractor catches its
 *     own Gemini error and returns `{ kind: 'extraction-failure' }`
 *     rather than throwing.
 *
 * Confidence cap (IIA-6):
 *   - Vision-only fills are capped at `IIA_VISION_CONFIDENCE_CAP`
 *     (0.60) until corroborated by a second image OR a CAR provider
 *     response within tolerance — see `crossCheckImageVsProvider` and
 *     the tolerances exported below. CAR providers aren't all wired
 *     yet (CAR-1..N still landing across waves), so the cross-check
 *     defaults to `'pending'` and the bump never fires when CAR has
 *     no response for the field. The bump goes to
 *     `IIA_VISION_CROSSCHECK_BOOST` (0.75).
 */
import { z } from 'zod';
import type { Schema } from '../geminiClient';
import { Type } from '../geminiClient';
import {
  analyzeImageEvidence,
  classifyImageKind,
  defaultMultimodalRunner,
  MIN_KIND_CONFIDENCE,
  type ImageGeminiRunner,
  type ImageKind,
} from './analyzeImageEvidence';

/* ── Public confidence + tolerance constants ───────────────────────── */

/**
 * Per-field tolerances for IIA-6 vision↔provider cross-check. Pack
 * spec table: BFE ±1ft, acreage ±5%, bearing capacity ±25 kPa,
 * capacity ±10%. Tests pin these so a future tightening is intentional.
 */
export const IIA_CROSSCHECK_TOLERANCES = Object.freeze({
  /** Base Flood Elevation, feet (absolute). */
  bfeFt: { type: 'absolute' as const, value: 1 },
  /** Parcel acreage, percent (fractional 0..1). */
  acreage: { type: 'percent' as const, value: 0.05 },
  /** Allowable bearing capacity, kPa (absolute). */
  bearingCapacityKPa: { type: 'absolute' as const, value: 25 },
  /** Capacity, megawatts, percent (fractional 0..1). */
  capacityMw: { type: 'percent' as const, value: 0.10 },
});

export type IiaCrossCheckFieldKey = keyof typeof IIA_CROSSCHECK_TOLERANCES;

/* ── Per-kind zod schemas ─────────────────────────────────────────── */

/**
 * FIRM panel — FEMA Flood Insurance Rate Map crop. All fields nullable
 * because a partial / cropped panel may only show one of them. Bounds
 * are physical — BFE outside 0..1000 ft is non-physical (the highest
 * BFE in the US system is ~250 ft); flood zone is a closed enum.
 */
export const FIRM_PANEL_SCHEMA = z.object({
  /** Flood zone designation. Closed enum mirrors FEMA's published
   *  set: X (unshaded), A/AE/AH/AO (riverine SFHA), V/VE (coastal
   *  SFHA), D (undetermined). */
  floodZone: z.enum(['X', 'A', 'AE', 'AH', 'AO', 'VE', 'V', 'D']).nullable(),
  /** Base Flood Elevation, feet. Physical bound: 0..1000 ft. */
  bfeFt: z.number().min(0).max(1000).nullable(),
  /** Vertical datum. NAVD88 is current; NGVD29 is the legacy datum
   *  panels were drawn against pre-1980s. */
  datum: z.enum(['NAVD88', 'NGVD29']).nullable(),
  /** 12-char panel id like `'05053C0140G'`. Free-string nullable to
   *  accept legacy panels with a different format. */
  panelId: z.string().nullable(),
  /** Effective date in any ISO-ish format the model can read off the
   *  panel margin. Free-string — downstream parser handles. */
  effectiveDate: z.string().nullable(),
  /** Free-text notes the model flagged as worth surfacing. */
  notes: z.string().nullable(),
});

export type FirmPanelExtract = z.infer<typeof FIRM_PANEL_SCHEMA>;

/**
 * Borehole log — geotechnical drill record. The pack's headline
 * structured fields plus an optional per-layer array (the log's body
 * is a stack of soil descriptions keyed by depth). Bounds match
 * physical limits: bearing capacity caps at 2000 kPa (above that is
 * intact rock — not what a borehole reports); depths cap at 200 m.
 */
export const BOREHOLE_LOG_SCHEMA = z.object({
  /** Top / representative USCS class. Closed enum — common datacenter
   *  geotech materials only; PT (peat) is included for risk flagging. */
  uscsClass: z.enum(['SP', 'SM', 'SC', 'ML', 'CL', 'CH', 'OL', 'OH', 'Pt']).nullable(),
  /** Allowable bearing capacity, kPa. Bound 0..2000. */
  bearingCapacityKPa: z.number().min(0).max(2000).nullable(),
  /** Borehole depth, metres. Bound 0..200. */
  boreholeDepthM: z.number().min(0).max(200).nullable(),
  /** Groundwater depth from surface, metres. Bound 0..200. */
  groundwaterDepthM: z.number().min(0).max(200).nullable(),
  /** Per-layer description rows. Optional; an empty array is valid. */
  layers: z.array(
    z.object({
      depthFromM: z.number().min(0).max(200),
      depthToM: z.number().min(0).max(200),
      description: z.string(),
      uscsClass: z.string().optional(),
    }),
  ),
  /** Drilling date the model read off the title block. */
  drillingDate: z.string().nullable(),
  notes: z.string().nullable(),
});

export type BoreholeLogExtract = z.infer<typeof BOREHOLE_LOG_SCHEMA>;

/**
 * Site map — recorded survey plat or boundary survey. Acreage in
 * acres (US-common); dimensions in feet (matches the typical plat
 * annotation). Bearings are emitted as free strings because the
 * format varies ("N 89° 23' 12" E", "N89-23-12E", "S2°W") — the
 * narrative consumer normalises later.
 */
export const SITE_MAP_SCHEMA = z.object({
  /** County parcel id / APN. Free string, nullable. */
  parcelId: z.string().nullable(),
  /** Recorded acreage. Bound 0..100_000 acres. */
  recordedAcreage: z.number().min(0).max(100_000).nullable(),
  /** Side dimensions as `{ side, lengthFt }` rows. */
  dimensions: z.array(
    z.object({
      side: z.string(),
      lengthFt: z.number().min(0).max(50_000),
    }),
  ),
  /** Easements crossing the parcel. `width_ft` may be null when the
   *  plat doesn't dimension it. */
  easements: z.array(
    z.object({
      type: z.string(),
      width_ft: z.number().min(0).max(500).nullable(),
      description: z.string(),
    }),
  ),
  /** Bearings as `{ side, bearing }` rows. */
  bearings: z.array(
    z.object({
      side: z.string(),
      bearing: z.string(),
    }),
  ),
  surveyDate: z.string().nullable(),
  surveyor: z.string().nullable(),
  notes: z.string().nullable(),
});

export type SiteMapExtract = z.infer<typeof SITE_MAP_SCHEMA>;

/**
 * Utility diagram — one-line / single-line electrical diagram or
 * interconnection sketch. Captures the upstream substation + the
 * interconnection point. Voltages capped at 765 kV (highest US
 * transmission tier); capacity capped at 10_000 MW.
 */
export const UTILITY_DIAGRAM_SCHEMA = z.object({
  substationName: z.string().nullable(),
  /** Voltage in kV. Bound 0..765. */
  voltageKv: z.number().min(0).max(765).nullable(),
  /** Capacity in MW. Bound 0..10_000. */
  capacityMw: z.number().min(0).max(10_000).nullable(),
  utilityName: z.string().nullable(),
  interconnectionPoint: z.string().nullable(),
  notes: z.string().nullable(),
});

export type UtilityDiagramExtract = z.infer<typeof UTILITY_DIAGRAM_SCHEMA>;

/* ── Gemini response schemas (mirror zod, narrower for the SDK) ──── */

const FIRM_PANEL_RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    floodZone: { type: Type.STRING, nullable: true },
    bfeFt: { type: Type.NUMBER, nullable: true },
    datum: { type: Type.STRING, nullable: true },
    panelId: { type: Type.STRING, nullable: true },
    effectiveDate: { type: Type.STRING, nullable: true },
    notes: { type: Type.STRING, nullable: true },
  },
};

const BOREHOLE_LOG_RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    uscsClass: { type: Type.STRING, nullable: true },
    bearingCapacityKPa: { type: Type.NUMBER, nullable: true },
    boreholeDepthM: { type: Type.NUMBER, nullable: true },
    groundwaterDepthM: { type: Type.NUMBER, nullable: true },
    layers: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          depthFromM: { type: Type.NUMBER },
          depthToM: { type: Type.NUMBER },
          description: { type: Type.STRING },
          uscsClass: { type: Type.STRING, nullable: true },
        },
        required: ['depthFromM', 'depthToM', 'description'],
      },
    },
    drillingDate: { type: Type.STRING, nullable: true },
    notes: { type: Type.STRING, nullable: true },
  },
  required: ['layers'],
};

const SITE_MAP_RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    parcelId: { type: Type.STRING, nullable: true },
    recordedAcreage: { type: Type.NUMBER, nullable: true },
    dimensions: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          side: { type: Type.STRING },
          lengthFt: { type: Type.NUMBER },
        },
        required: ['side', 'lengthFt'],
      },
    },
    easements: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          type: { type: Type.STRING },
          width_ft: { type: Type.NUMBER, nullable: true },
          description: { type: Type.STRING },
        },
        required: ['type', 'description'],
      },
    },
    bearings: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          side: { type: Type.STRING },
          bearing: { type: Type.STRING },
        },
        required: ['side', 'bearing'],
      },
    },
    surveyDate: { type: Type.STRING, nullable: true },
    surveyor: { type: Type.STRING, nullable: true },
    notes: { type: Type.STRING, nullable: true },
  },
  required: ['dimensions', 'easements', 'bearings'],
};

const UTILITY_DIAGRAM_RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    substationName: { type: Type.STRING, nullable: true },
    voltageKv: { type: Type.NUMBER, nullable: true },
    capacityMw: { type: Type.NUMBER, nullable: true },
    utilityName: { type: Type.STRING, nullable: true },
    interconnectionPoint: { type: Type.STRING, nullable: true },
    notes: { type: Type.STRING, nullable: true },
  },
};

/* ── Per-kind prompt builders ─────────────────────────────────────── */

function firmPanelPrompt(): string {
  return (
    "This image has been classified as a FEMA FIRM (Flood Insurance Rate Map) panel.\n" +
    'Extract structured fields. NEVER guess — return null for any field you cannot read directly.\n' +
    '- floodZone: one of [X, A, AE, AH, AO, VE, V, D] for the site of interest (look for shaded zones, labels).\n' +
    '- bfeFt: Base Flood Elevation in feet (per-zone label or contour). 0..1000.\n' +
    '- datum: NAVD88 or NGVD29 (panel margin).\n' +
    '- panelId: 12-char code like 05053C0140G (upper margin).\n' +
    '- effectiveDate: panel effective date (margin).\n' +
    '- notes: anything else worth surfacing (1-2 sentences max).\n\n' +
    'Return JSON matching the schema. Unreadable fields MUST be null.'
  );
}

function boreholePrompt(): string {
  return (
    'This image has been classified as a geotechnical borehole log.\n' +
    'Extract structured fields. NEVER guess — return null when not directly readable.\n' +
    '- uscsClass: top / representative USCS class (SP/SM/SC/ML/CL/CH/OL/OH/Pt).\n' +
    '- bearingCapacityKPa: allowable bearing capacity in kPa. 0..2000.\n' +
    '- boreholeDepthM: total borehole depth in metres. 0..200.\n' +
    '- groundwaterDepthM: depth to groundwater from surface, metres. 0..200.\n' +
    '- layers: per-row [{ depthFromM, depthToM, description, uscsClass? }]. Emit [] when no layer table is visible.\n' +
    '- drillingDate: date the log was drilled.\n' +
    '- notes: any qualifiers (refusal depth, water seepage, etc).\n\n' +
    'Return JSON matching the schema. Unreadable fields MUST be null.'
  );
}

function siteMapPrompt(): string {
  return (
    'This image has been classified as a recorded survey plat / boundary survey.\n' +
    'Extract structured fields. NEVER guess — return null when not directly readable.\n' +
    '- parcelId: county APN / parcel id.\n' +
    '- recordedAcreage: recorded acreage in acres.\n' +
    '- dimensions: [{ side: "N", lengthFt }] for each labelled boundary segment. [] when none visible.\n' +
    '- easements: [{ type, width_ft (or null), description }] for each easement crossing the parcel.\n' +
    '- bearings: [{ side, bearing }] verbatim (preserve degree/minute/second formatting).\n' +
    '- surveyDate, surveyor: title block.\n' +
    '- notes: anything else worth surfacing.\n\n' +
    'Return JSON matching the schema.'
  );
}

function utilityDiagramPrompt(): string {
  return (
    'This image has been classified as an electrical one-line / single-line diagram or interconnection sketch.\n' +
    'Extract structured fields. NEVER guess — return null when not directly readable.\n' +
    '- substationName: name printed on the upstream substation bus.\n' +
    '- voltageKv: highest visible voltage at the interconnection point, in kV. 0..765.\n' +
    '- capacityMw: ATC / firm capacity at the interconnection point, in MW. 0..10000.\n' +
    '- utilityName: serving utility name (legend / title block).\n' +
    '- interconnectionPoint: free-text label of the POI (POI bus name, breaker id, etc).\n' +
    '- notes: anything else worth surfacing.\n\n' +
    'Return JSON matching the schema. Unreadable fields MUST be null.'
  );
}

/* ── Extractor result discriminated union ─────────────────────────── */

/**
 * The discriminated union every per-kind extractor returns.
 *
 * - `'firm-panel'` / `'borehole-log'` / `'site-map'` / `'utility-diagram'`:
 *   successful structured extraction. `data` is the zod-validated
 *   typed result; `confidence` is the classifier confidence (already
 *   clamped to [0, 1] by IIA-1).
 *
 * - `'extraction-failure'`: the extractor was called but did not
 *   produce a usable structured result. The caller MUST fall back to
 *   the IIA-3 free-text path (free-text summary into
 *   `siteContext.claims[]` with `kind: 'image-observation'`).
 */
export type ImageExtractionResult =
  | { kind: 'firm-panel'; data: FirmPanelExtract; confidence: number }
  | { kind: 'borehole-log'; data: BoreholeLogExtract; confidence: number }
  | { kind: 'site-map'; data: SiteMapExtract; confidence: number }
  | { kind: 'utility-diagram'; data: UtilityDiagramExtract; confidence: number }
  | { kind: 'extraction-failure'; reason: ExtractionFailureReason };

export type ExtractionFailureReason =
  | 'classifier-uncertain'
  | 'no-structured-extractor'
  | 'gemini-error'
  | 'schema-validation-failed'
  | 'empty-blob'
  | 'cancelled';

/* ── Internal Gemini caller ───────────────────────────────────────── */

/**
 * Internal: call the multimodal runner with the supplied response
 * schema + prompt. Mirrors IIA-1's blob → base64 path exactly so the
 * cost guards (timeout, structured-JSON, runner injection) are honoured.
 */
async function callMultimodal(
  imageBlob: Blob,
  prompt: string,
  schema: Schema,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<unknown | null> {
  const cancelled = options?.cancelled ?? (() => false);
  if (cancelled()) return null;
  if (!imageBlob || imageBlob.size === 0) return null;
  if (imageBlob.type && !imageBlob.type.startsWith('image/')) return null;
  const runner = options?.runner ?? defaultMultimodalRunner;
  try {
    const buf = await imageBlob.arrayBuffer();
    const base64 = typeof Buffer !== 'undefined'
      ? Buffer.from(buf).toString('base64')
      : (() => {
          let binary = '';
          const bytes = new Uint8Array(buf);
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
          return typeof btoa === 'function' ? btoa(binary) : '';
        })();
    if (!base64) return null;
    const mimeType = imageBlob.type || 'image/png';
    const raw = await runner({
      base64Data: base64,
      mimeType,
      prompt,
      schema,
      timeoutMs: 30_000,
    });
    return raw;
  } catch (e) {
    if (!cancelled()) {
      console.warn('[imageExtractors] multimodal call failed:', (e as Error)?.message);
    }
    return null;
  }
}

/* ── Per-kind extractors ──────────────────────────────────────────── */

/**
 * Extract a FIRM panel.
 *
 * Maps to checklist requirement `'flood-cert'` (Base Flood Elevation
 * certificate). Caller wires `bfeFt` into the CAR-2 cross-check.
 */
export async function extractFirmPanel(
  imageBlob: Blob,
  classifierConfidence: number,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<ImageExtractionResult> {
  if (!imageBlob || imageBlob.size === 0) {
    return { kind: 'extraction-failure', reason: 'empty-blob' };
  }
  if (options?.cancelled?.()) {
    return { kind: 'extraction-failure', reason: 'cancelled' };
  }
  const raw = await callMultimodal(imageBlob, firmPanelPrompt(), FIRM_PANEL_RESPONSE_SCHEMA, options);
  if (raw == null) return { kind: 'extraction-failure', reason: 'gemini-error' };
  const parsed = FIRM_PANEL_SCHEMA.safeParse(raw);
  if (!parsed.success) {
    return { kind: 'extraction-failure', reason: 'schema-validation-failed' };
  }
  return {
    kind: 'firm-panel',
    data: parsed.data,
    confidence: clampConfidence(classifierConfidence),
  };
}

/**
 * Extract a geotechnical borehole log. Maps to requirement `'geotech'`.
 */
export async function extractBorehole(
  imageBlob: Blob,
  classifierConfidence: number,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<ImageExtractionResult> {
  if (!imageBlob || imageBlob.size === 0) {
    return { kind: 'extraction-failure', reason: 'empty-blob' };
  }
  if (options?.cancelled?.()) {
    return { kind: 'extraction-failure', reason: 'cancelled' };
  }
  const raw = await callMultimodal(imageBlob, boreholePrompt(), BOREHOLE_LOG_RESPONSE_SCHEMA, options);
  if (raw == null) return { kind: 'extraction-failure', reason: 'gemini-error' };
  // Pre-coerce: Gemini sometimes returns layers: null instead of [].
  if (raw && typeof raw === 'object' && (raw as Record<string, unknown>).layers == null) {
    (raw as Record<string, unknown>).layers = [];
  }
  const parsed = BOREHOLE_LOG_SCHEMA.safeParse(raw);
  if (!parsed.success) {
    return { kind: 'extraction-failure', reason: 'schema-validation-failed' };
  }
  return {
    kind: 'borehole-log',
    data: parsed.data,
    confidence: clampConfidence(classifierConfidence),
  };
}

/**
 * Extract a survey plat / site map. Maps to requirement `'parcel-survey'`.
 */
export async function extractSiteMap(
  imageBlob: Blob,
  classifierConfidence: number,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<ImageExtractionResult> {
  if (!imageBlob || imageBlob.size === 0) {
    return { kind: 'extraction-failure', reason: 'empty-blob' };
  }
  if (options?.cancelled?.()) {
    return { kind: 'extraction-failure', reason: 'cancelled' };
  }
  const raw = await callMultimodal(imageBlob, siteMapPrompt(), SITE_MAP_RESPONSE_SCHEMA, options);
  if (raw == null) return { kind: 'extraction-failure', reason: 'gemini-error' };
  // Pre-coerce: empty arrays sometimes arrive as null from the model.
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (r.dimensions == null) r.dimensions = [];
    if (r.easements == null) r.easements = [];
    if (r.bearings == null) r.bearings = [];
  }
  const parsed = SITE_MAP_SCHEMA.safeParse(raw);
  if (!parsed.success) {
    return { kind: 'extraction-failure', reason: 'schema-validation-failed' };
  }
  return {
    kind: 'site-map',
    data: parsed.data,
    confidence: clampConfidence(classifierConfidence),
  };
}

/**
 * Extract a one-line / utility diagram. Maps to requirement `'utility-letter'`.
 */
export async function extractUtilityDiagram(
  imageBlob: Blob,
  classifierConfidence: number,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<ImageExtractionResult> {
  if (!imageBlob || imageBlob.size === 0) {
    return { kind: 'extraction-failure', reason: 'empty-blob' };
  }
  if (options?.cancelled?.()) {
    return { kind: 'extraction-failure', reason: 'cancelled' };
  }
  const raw = await callMultimodal(imageBlob, utilityDiagramPrompt(), UTILITY_DIAGRAM_RESPONSE_SCHEMA, options);
  if (raw == null) return { kind: 'extraction-failure', reason: 'gemini-error' };
  const parsed = UTILITY_DIAGRAM_SCHEMA.safeParse(raw);
  if (!parsed.success) {
    return { kind: 'extraction-failure', reason: 'schema-validation-failed' };
  }
  return {
    kind: 'utility-diagram',
    data: parsed.data,
    confidence: clampConfidence(classifierConfidence),
  };
}

/* ── Dispatcher ───────────────────────────────────────────────────── */

/**
 * `ImageKind` → which structured extractor (if any) to run, plus the
 * requirement id the extractor is expected to populate. Kinds NOT in
 * this map fall through to IIA-3 free-text fallback.
 */
const KIND_TO_EXTRACTOR: ReadonlyMap<
  ImageKind,
  { requirementId: string; run: (b: Blob, c: number, o?: { runner?: ImageGeminiRunner; cancelled?: () => boolean }) => Promise<ImageExtractionResult> }
> = new Map([
  ['flood-map',        { requirementId: 'flood-cert',     run: extractFirmPanel       }],
  ['borehole-log',     { requirementId: 'geotech',        run: extractBorehole        }],
  ['survey-plat',      { requirementId: 'parcel-survey',  run: extractSiteMap         }],
  ['one-line-diagram', { requirementId: 'utility-letter', run: extractUtilityDiagram  }],
]);

/**
 * Map an `ImageKind` to the checklist requirement id (or `null` when
 * no structured extractor exists for that kind). Exported so callers
 * (e.g. `documentUpload.ts`) can stamp `intendedRequirementIds`
 * without re-deriving the mapping.
 */
export function requirementIdForImageKind(kind: ImageKind): string | null {
  return KIND_TO_EXTRACTOR.get(kind)?.requirementId ?? null;
}

/**
 * Full IIA-4 pipeline outcome that `documentUpload.ts` persists onto
 * `SiteDocument.iiaResult`. Discriminated by `attemptResult` so the
 * audit dashboard can roll up "how many uploads got real structure"
 * vs "how many fell through to free-text".
 */
export interface IiaPipelineResult {
  classification: { kind: ImageKind; confidence: number };
  /** Structured extraction (when classifier was confident AND a
   *  per-kind extractor exists). */
  extraction?: ImageExtractionResult;
  /** Free-text observation (IIA-3 fallback). Populated when the
   *  classifier was `'other'` / low-confidence OR no structured
   *  extractor exists for the classified kind OR a per-kind extractor
   *  returned `extraction-failure`. `confidence` is capped at
   *  `IIA_VISION_FREE_TEXT_CONFIDENCE_CAP` (0.55) — one notch below
   *  the per-kind structured cap (0.60) so the audit dashboard can
   *  distinguish "structured extraction" from "free-text observation".
   *  `userApproved: false` until the user accepts via the IIA-5
   *  per-image extraction card. */
  observation?: {
    observationText: string;
    relevantSections?: readonly string[];
    confidence: number;
    userApproved: false;
  };
  /** Which requirement id (if any) the structured extraction targets. */
  targetRequirementId: string | null;
  /** ISO timestamp. */
  runAt: string;
  attemptResult: 'success' | 'empty' | 'classifier-uncertain' | 'extraction-failed';
}

/**
 * IIA-4 pipeline entry point.
 *
 * Two-pass flow per the pack:
 *   1. classify (IIA-1 `classifyImageKind`).
 *   2. when `kind !== 'other'` AND `confidence >= MIN_KIND_CONFIDENCE`
 *      AND a per-kind extractor exists → run structured extraction.
 *   3. otherwise → return an IIA-3 free-text observation built from
 *      IIA-1's `analyzeImageEvidence` (which the caller will write
 *      onto `siteContext.claims[]` as `kind: 'image-observation'`).
 *
 * Never throws — every failure path collapses to a structured result
 * the caller can persist via `stripUndefined()`.
 */
export async function dispatchImageExtraction(
  imageBlob: Blob,
  options?: {
    kindHint?: ImageKind;
    runner?: ImageGeminiRunner;
    cancelled?: () => boolean;
  },
): Promise<IiaPipelineResult> {
  const cancelled = options?.cancelled ?? (() => false);
  const runAt = new Date().toISOString();
  if (!imageBlob || imageBlob.size === 0) {
    return {
      classification: { kind: 'other', confidence: 0 },
      targetRequirementId: null,
      runAt,
      attemptResult: 'empty',
    };
  }
  // 1) classify (reuses IIA-1's runner injection).
  const classification = await classifyImageKind(imageBlob, {
    kindHint: options?.kindHint,
    runner: options?.runner,
    cancelled,
  });
  // Classifier uncertain or 'other' — IIA-3 free-text fallback.
  if (classification.kind === 'other' || classification.confidence < MIN_KIND_CONFIDENCE) {
    const observation = await buildFreeTextObservation(imageBlob, options);
    // DIM-1 — a CONFIDENT 'other' whose free-text observation landed is a
    // successful outcome (mirrors the known-kind-without-extractor branch
    // below). The old unconditional 'extraction-failed' stamped the live
    // Kinder Morgan document screenshot as a failure even though the OCR
    // pass extracted every figure correctly.
    const attemptResult: IiaPipelineResult['attemptResult'] =
      classification.confidence < MIN_KIND_CONFIDENCE
        ? 'classifier-uncertain'
        : observation
          ? 'success'
          : 'extraction-failed';
    return {
      classification,
      ...(observation ? { observation } : {}),
      targetRequirementId: null,
      runAt,
      attemptResult,
    };
  }
  // 2) structured extractor (if registered for this kind).
  const route = KIND_TO_EXTRACTOR.get(classification.kind);
  if (!route) {
    // Known kind with no structured extractor (site-photo, aerial,
    // kmz-overlay, protest-flyer, permit-stamp, document-image) —
    // IIA-3 fallback. For document-image the free-text observation IS
    // the payload: it carries the OCR transcription (DIM-1).
    const observation = await buildFreeTextObservation(imageBlob, options);
    return {
      classification,
      ...(observation ? { observation } : {}),
      targetRequirementId: null,
      runAt,
      attemptResult: 'success',
    };
  }
  const extraction = await route.run(imageBlob, classification.confidence, options);
  if (extraction.kind === 'extraction-failure') {
    // Per-kind extractor returned a failure — still emit free-text
    // observation so the user gets SOMETHING in SED.
    const observation = await buildFreeTextObservation(imageBlob, options);
    return {
      classification,
      extraction,
      ...(observation ? { observation } : {}),
      targetRequirementId: route.requirementId,
      runAt,
      attemptResult: 'extraction-failed',
    };
  }
  return {
    classification,
    extraction,
    targetRequirementId: route.requirementId,
    runAt,
    attemptResult: 'success',
  };
}

/**
 * Build the IIA-3 free-text observation by reusing IIA-1's
 * `analyzeImageEvidence` (which already does a per-kind extraction
 * pass when classification is confident). For the IIA-3 fallback we
 * collapse the findings into a single observationText string.
 *
 * Returns null when the analyzer also failed — caller stores no
 * observation in that case.
 *
 * The returned `confidence` is the analyzer's classifier confidence
 * clamped to `IIA_VISION_FREE_TEXT_CONFIDENCE_CAP` (0.55) per pack
 * §IIA-3 / §IIA-5 — free-text is always one notch below the per-kind
 * structured cap.
 */
async function buildFreeTextObservation(
  imageBlob: Blob,
  options?: { kindHint?: ImageKind; runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<IiaPipelineResult['observation'] | null> {
  try {
    const evidence = await analyzeImageEvidence(imageBlob, options?.kindHint, {
      ...(options?.runner ? { runner: options.runner } : {}),
      ...(options?.cancelled ? { cancelled: options.cancelled } : {}),
    });
    const parts: string[] = [];
    if (evidence.extractedText && evidence.extractedText.trim()) {
      parts.push(evidence.extractedText.slice(0, 800));
    }
    for (const f of evidence.findings) {
      parts.push(`${f.fieldPath}: ${stringifyValue(f.value)} (${f.evidenceQuote})`);
    }
    if (parts.length === 0) return null;
    const observationText = parts.join('\n').slice(0, 2000);
    return {
      observationText,
      confidence: clampFreeTextConfidence(evidence.kindConfidence ?? 0),
      userApproved: false,
    };
  } catch (e) {
    if (!options?.cancelled?.()) {
      console.warn('[imageExtractors] free-text fallback failed:', (e as Error)?.message);
    }
    return null;
  }
}

/**
 * IIA-3 — standalone free-text observation extractor.
 *
 * Direct entry point for callers that already know they want the
 * free-text path (e.g. the IIA-5 "Re-extract as free text" button on
 * the per-image card). Routes through the same
 * `defaultMultimodalRunner` IIA-1 + IIA-2 use, so the cost guards
 * stay centralised. The confidence is clamped to
 * `IIA_VISION_FREE_TEXT_CONFIDENCE_CAP` (0.55).
 *
 * Returns `null` (not a thrown error) when:
 *   - the blob is empty,
 *   - the blob's mimeType isn't `image/*`,
 *   - the underlying multimodal call failed,
 *   - the analyzer found nothing to summarise.
 *
 * Never throws — IIA-5 callers expect a stable resolve.
 */
export async function extractFreeTextFromImage(
  imageBlob: Blob,
  options?: { kindHint?: ImageKind; runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<{
  observationText: string;
  confidence: number;
  userApproved: false;
} | null> {
  if (!imageBlob || imageBlob.size === 0) return null;
  if (imageBlob.type && !imageBlob.type.startsWith('image/')) return null;
  if (options?.cancelled?.()) return null;
  const obs = await buildFreeTextObservation(imageBlob, options);
  if (!obs) return null;
  // The internal helper already shapes + caps; surface the same shape
  // to external callers but strip the optional relevantSections that
  // only the dispatcher-fed pipeline path populates today.
  return {
    observationText: obs.observationText,
    confidence: obs.confidence,
    userApproved: false,
  };
}

function stringifyValue(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v).slice(0, 200);
  } catch {
    return '[unstringifiable]';
  }
}

/* ── Confidence cap helpers ──────────────────────────────────────── */

/**
 * Per-kind structured-extraction confidence cap (pack §IIA-6) — the
 * exported constant so the IIA-5 UI, the IIA-3 free-text fallback, and
 * the IIA-6 cross-check helper all read one number.
 */
export const IIA_VISION_CONFIDENCE_CAP = 0.6;

/**
 * Free-text (IIA-3) fallback confidence cap. ONE NOTCH below the
 * per-kind cap so the audit dashboard can distinguish "vision extracted
 * structured fields" (capped at 0.60) from "vision wrote a free-text
 * observation we have no schema for" (capped at 0.55). Pack hard rule
 * for IIA-3 + IIA-5.
 */
export const IIA_VISION_FREE_TEXT_CONFIDENCE_CAP = 0.55;

/**
 * Clamp the per-kind extractor's reported confidence to
 * `IIA_VISION_CONFIDENCE_CAP` (0.6) per pack §IIA-6 — never let a
 * vision-only extraction self-report above the cap. Tests pin this.
 */
function clampConfidence(raw: number): number {
  // Number.isFinite guard per HARD RULE — the zod schemas enforce
  // bounds on PAYLOAD fields, but the confidence parameter here is a
  // call-site input (classifierConfidence) so we re-check.
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(IIA_VISION_CONFIDENCE_CAP, raw));
}

/**
 * Clamp the IIA-3 free-text observation confidence to
 * `IIA_VISION_FREE_TEXT_CONFIDENCE_CAP` (0.55). Used by both the
 * dispatcher's fallback path and `extractFreeTextFromImage` so the
 * "image is something we have no schema for" surface NEVER inflates.
 */
function clampFreeTextConfidence(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(IIA_VISION_FREE_TEXT_CONFIDENCE_CAP, raw));
}

/* ── IIA-6 cross-check stub ──────────────────────────────────────── */

/**
 * Compare a vision-extracted value against a CAR provider response
 * for the same field. Per pack §IIA-6:
 *   - within tolerance → confidence bump to `IIA_VISION_CROSSCHECK_BOOST` (0.75).
 *   - out of tolerance → emit `UFC`-style conflict.
 *   - no provider response yet → `'pending'` (no bump, no conflict).
 *
 * CAR providers aren't all wired this turn (CAR-1 NRCS soils landed
 * 2026-05-19, FEMA NFHL + HIFLD still on the queue), so the stub
 * defaults to `'pending'` when `providerValue` is `undefined`. The
 * caller (CAF-5 pipeline writer) wires the real provider response
 * once it has the AggregatedSiteData slot keyed by field.
 */
export interface CrossCheckArgs {
  field: IiaCrossCheckFieldKey;
  imageValue: number | null;
  providerValue: number | null | undefined;
}

export interface CrossCheckResult {
  agreement: 'within-tolerance' | 'out-of-tolerance' | 'pending';
  imageValue: number | null;
  providerValue: number | null | undefined;
  /** Delta between values when both present — units match the field. */
  delta?: number;
  /** Confidence bump the caller MAY stamp on
   *  `RequirementCoverage.crossCheck.confidenceBoost`. Only set on
   *  `'within-tolerance'`. */
  confidenceBoost?: number;
}

export function crossCheckImageVsProvider(args: CrossCheckArgs): CrossCheckResult {
  const tolerance = IIA_CROSSCHECK_TOLERANCES[args.field];
  // Pending: no provider response, or either side missing.
  if (
    args.providerValue == null ||
    args.imageValue == null ||
    !Number.isFinite(args.providerValue as number) ||
    !Number.isFinite(args.imageValue)
  ) {
    return {
      agreement: 'pending',
      imageValue: args.imageValue,
      providerValue: args.providerValue,
    };
  }
  const img = args.imageValue;
  const prov = args.providerValue as number;
  const delta = Math.abs(img - prov);
  let withinTolerance: boolean;
  if (tolerance.type === 'absolute') {
    withinTolerance = delta <= tolerance.value;
  } else {
    // Percent tolerance — divide by the provider value (the ground
    // truth) so a 5% deviation on a 100-acre parcel is 5 acres not
    // 5.something. Guard against zero.
    const base = Math.max(Math.abs(prov), 1e-9);
    withinTolerance = (delta / base) <= tolerance.value;
  }
  if (withinTolerance) {
    return {
      agreement: 'within-tolerance',
      imageValue: img,
      providerValue: prov,
      delta,
      // The boost is the delta from the post-cap default (0.6) to
      // the cross-check ceiling (0.75). Caller stamps this on
      // `coverage.crossCheck.confidenceBoost`.
      confidenceBoost: 0.15,
    };
  }
  return {
    agreement: 'out-of-tolerance',
    imageValue: img,
    providerValue: prov,
    delta,
  };
}
